import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { IconAlertTriangle, IconArrowRight, IconCheck, IconTrash, IconX } from '@tabler/icons-react';
import LoadingIndicator from '../LoadingIndicator';

export default function ReviewModal({
  isOpen,
  onClose,
  operation,
  items,
  cabangList,
  onConfirm,
  loading
}) {
  const [formData, setFormData] = useState({
    cabang_id: '',
    keterangan: '',
    nomor_rak: ''
  });
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ cabang_id: '', keterangan: '', nomor_rak: '' });
      setShowConfirm(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (operation === 'hapus' && !formData.keterangan.trim()) {
      alert('Keterangan wajib diisi untuk penghapusan');
      return;
    }
    if ((operation === 'transfer' || operation === 'pindah') && !formData.cabang_id) {
      alert('Pilih cabang tujuan');
      return;
    }
    if (operation === 'pindah_baki' && !formData.nomor_rak) {
      alert('Pilih nomor baki tujuan');
      return;
    }
    setShowConfirm(true);
  };

  const handleSubmit = () => {
    onConfirm(formData);
  };

  const getOperationTitle = () => {
    switch (operation) {
      case 'transfer': return 'Transfer ke Cabang Lain';
      case 'pindah': return 'Pindah Etalase / Brankas';
      case 'pindah_baki': return 'Pindah ke BAKI Lain';
      case 'hapus': return 'Hapus Barang';
      default: return 'Operasi Barang';
    }
  };

  const getOperationIcon = () => {
    switch (operation) {
      case 'transfer': return <IconArrowRight className="w-6 h-6" />;
      case 'pindah': return <IconArrowRight className="w-6 h-6" />;
      case 'pindah_baki': return <IconCheck className="w-6 h-6" />;
      case 'hapus': return <IconTrash className="w-6 h-6" />;
      default: return <IconCheck className="w-6 h-6" />;
    }
  };

  const getOperationColor = () => {
    switch (operation) {
      case 'transfer': return 'amber';
      case 'pindah': return 'blue';
      case 'pindah_baki': return 'emerald';
      case 'hapus': return 'red';
      default: return 'slate';
    }
  };

  const color = getOperationColor();

  return (
    <>
      {loading && <LoadingIndicator />}
      <Transition appear show={isOpen && !showConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center p-4 min-h-screen">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden bg-white dark:bg-slate-900 sm:rounded-xl p-0 text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh] border dark:border-slate-800">
                  {/* Header */}
                  <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex items-center justify-between sticky top-0 z-10 w-full">
                    <div>
                      <Dialog.Title as="h2" className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">
                        {getOperationTitle()}
                      </Dialog.Title>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        {items.length === 1 ? '1 barang akan diproses' : `${items.length} barang akan diproses`}
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
                    >
                      <IconX className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-5 overflow-y-auto flex-1 bg-slate-50/50 dark:bg-slate-900/50">
                    {/* Items Table */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60 rounded-lg overflow-hidden shadow-sm mb-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                          <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700/60">
                            <tr>
                              <th scope="col" className="px-3 py-2 font-semibold w-12 text-center">No</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Kode</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Barang</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 text-right align-middle">Berat</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Cabang</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {items.map((item, i) => (
                              <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors bg-white dark:bg-slate-900">
                                <td className="px-3 py-2 text-center text-slate-400 dark:text-slate-500">{i + 1}</td>
                                <td className="px-3 py-2 font-medium border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-700 dark:text-slate-300">{item.kode_item}</td>
                                <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-600 dark:text-slate-400 truncate max-w-[150px]">{item.jenis_kategori?.nama_jenis}</td>
                                <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-right tabular-nums text-slate-700 dark:text-slate-300">{item.berat} gr</td>
                                <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-600 dark:text-slate-400">{item.cabang?.nama_cabang}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Additional Form Fields */}
                    {(operation === 'transfer' || operation === 'pindah') && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                          Cabang Tujuan
                        </label>
                        <select
                          value={formData.cabang_id}
                          onChange={(e) => setFormData({ ...formData, cabang_id: e.target.value })}
                          className="w-full h-10 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 dark:focus:border-amber-500 cursor-pointer appearance-none shadow-sm dark:text-slate-200"
                        >
                          <option value="">Pilih Cabang Tujuan...</option>
                          {cabangList?.map((cb) => (
                            <option value={cb.id} key={cb.id}>
                              {cb.nama_cabang}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {operation === 'pindah_baki' && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                          Nomor BAKI Tujuan
                        </label>
                        <select
                          value={formData.nomor_rak}
                          onChange={(e) => setFormData({ ...formData, nomor_rak: e.target.value })}
                          className="w-full h-10 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 dark:focus:border-emerald-500 cursor-pointer appearance-none shadow-sm dark:text-slate-200"
                        >
                          <option value="">Pilih Baki Tujuan...</option>
                          {[...Array(20)].map((_, i) => (
                            <option value={i + 1} key={i + 1}>
                              Baki {i + 1}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {operation === 'hapus' && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                          Alasan Penghapusan (Keterangan)
                        </label>
                        <textarea
                          rows="3"
                          value={formData.keterangan}
                          onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-4 focus:ring-red-500/10 focus:border-red-500 transition-all resize-none text-sm outline-none shadow-sm"
                          placeholder="Contoh: Barang rusak, salah input, atau stok habis..."
                        ></textarea>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-500 italic flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          Keterangan ini wajib diisi untuk dokumentasi sistem.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex flex-col-reverse sm:flex-row justify-end gap-3 sticky bottom-0 w-full z-10">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={onClose}
                      className="w-full sm:w-auto h-11 sm:h-10 px-6 inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 transition-colors disabled:opacity-50"
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleConfirm}
                      className={`w-full sm:w-auto h-11 sm:h-10 px-8 inline-flex items-center justify-center gap-2 rounded-lg font-bold text-white bg-gradient-to-r from-${color}-500 to-${color}-600 hover:from-${color}-400 hover:to-${color}-500 active:from-${color}-600 active:to-${color}-700 shadow-md shadow-${color}-500/20 transition-all disabled:from-slate-300 disabled:to-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed`}
                    >
                      {getOperationIcon()}
                      <span>Lanjut Proses</span>
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Dialog */}
      <Transition appear show={showConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => !loading && setShowConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-white dark:bg-slate-900 p-6 text-left align-middle shadow-xl transition-all border dark:border-slate-800">
                  <div className="flex items-center justify-center mb-4 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-500/20 mx-auto">
                    <IconAlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-slate-900 dark:text-slate-100 text-center"
                  >
                    Konfirmasi {getOperationTitle()}
                  </Dialog.Title>
                  <div className="mt-2 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Anda yakin akan {operation === 'hapus' ? 'menghapus' : 'memproses'} <span className="font-bold text-slate-700 dark:text-slate-300">{items.length}</span> barang ini?
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-xl border border-transparent bg-slate-100 dark:bg-slate-800 px-4 py-2.5 text-sm font-medium text-slate-900 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:opacity-50 transition-colors"
                      onClick={() => setShowConfirm(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-xl border border-transparent bg-amber-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 shadow-md shadow-amber-500/20 disabled:opacity-50 transition-all"
                      onClick={handleSubmit}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                          <span>Proses...</span>
                        </div>
                      ) : (
                        <span>Ya, Proses</span>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
